import React from 'react';

import { LegacyTable, ReportSection, type LegacyCell } from './ReportSection';

import type { LeadPivotResponse } from '@utazzo/shared';

interface PivotTableProps {
  title: string;
  pivot: LeadPivotResponse | undefined;
  loading: boolean;
  /**
   * Optional per-cell renderer, e.g. to turn counts into drill buttons.
   * `rowIndex` null = the bottom totals row; `colIndex` null = the right Total
   * column (both null = grand total). Defaults to the raw count (totals bold).
   */
  renderCell?: (count: number, rowIndex: number | null, colIndex: number | null) => LegacyCell;
}

/**
 * Agent-cross count matrix in the legacy `db-angular-pivot-table` look.
 * The server computes the cells; this just renders them with totals.
 */
export const PivotTable = ({
  title,
  pivot,
  loading,
  renderCell,
}: PivotTableProps): React.JSX.Element => {
  const bodyCell = (count: number, rowIndex: number | null, colIndex: number | null): LegacyCell =>
    renderCell ? renderCell(count, rowIndex, colIndex) : count;
  const totalCell = (
    count: number,
    rowIndex: number | null,
    colIndex: number | null,
  ): LegacyCell =>
    renderCell
      ? renderCell(count, rowIndex, colIndex)
      : { node: <strong>{count}</strong>, sort: count };
  return (
    <ReportSection title={title}>
      <LegacyTable
        loading={loading || !pivot}
        emptyText="No leads in this range."
        headers={['Agent', ...(pivot?.columns ?? []), 'Total']}
        rows={(pivot?.rows ?? []).map((agentName, rowIndex) => [
          agentName,
          ...(pivot?.columns ?? []).map((_, colIndex) =>
            bodyCell(pivot?.cells[rowIndex]?.[colIndex] ?? 0, rowIndex, colIndex),
          ),
          totalCell(pivot?.rowTotals[rowIndex] ?? 0, rowIndex, null),
        ])}
        totals={
          pivot && [
            'Total',
            ...pivot.columnTotals.map((total, colIndex) => totalCell(total, null, colIndex)),
            totalCell(pivot.grandTotal, null, null),
          ]
        }
      />
    </ReportSection>
  );
};
